/* Copyright (c) 2020-present CloudIO Inc */

import { CommonProps } from 'componentregistry/layout/Sidebar/panels/PropertiesView/CommonProperties';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { isEmpty } from 'utils/isEmpty';
import {
  PropertyDefinition,
  PropertyDefinitions,
} from '../../registry/Component.type';
import { Box } from './Box';

export const alignItems: PropertyDefinition = {
  label: 'Align Items',
  type: 'select',
  required: true,
  options: toOptions([
    'baseline',
    'center',
    'end',
    'flex-end',
    'flex-start',
    'inherit',
    'initial',
    'normal',
    'revert',
    'self-end',
    'self-start',
    'start',
    'stretch',
    'unset',
  ]),
  category: 'Flex',
};

export const alignContent: PropertyDefinition = {
  label: 'Align Content',
  type: 'select',
  options: toOptions([
    'auto',
    'baseline',
    'center',
    'end',
    'flex-end',
    'flex-start',
    'inherit',
    'initial',
    'normal',
    'revert',
    'self-end',
    'self-start',
    'start',
    'stretch',
    'unset',
  ]),
  category: 'Flex',
};
export const justifyContent: PropertyDefinition = {
  label: 'Justify Content',
  type: 'select',
  required: true,
  options: toOptions([
    'baseline',
    'center',
    'end',
    'flex-end',
    'flex-start',
    'inherit',
    'initial',
    'normal',
    'revert',
    'space-around',
    'space-between',
    'space-evenly',
    'start',
    'stretch',
    'unset',
  ]),
  category: 'Flex',
};

export const flexDirection: PropertyDefinition = {
  label: 'Flex Direction',
  type: 'select',
  required: true,
  options: toOptions([
    'column',
    'column-reverse',
    'inherit',
    'initial',
    'revert',
    'row',
    'row-reverse',
    'unset',
  ]),
  category: 'Flex',
};

export const gap: PropertyDefinition = {
  label: 'Gap',
  type: 'string',
  placeholder: 'e.g. 8px',
  helpText: 'The flex gap to be added between the child components. e.g. 8px',
  category: 'Flex',
};

export const BoxProps: PropertyDefinitions<Omit<Box, keyof CommonProps>> = {
  component: {
    label: 'Component',
    type: 'select',
    options: { span: 'Span', div: 'Div', img: 'Image' },
  },
  alt: {
    label: 'Alt',
    type: 'expression',
    returnType: async () => 'string',
  },
  src: {
    label: 'Src',
    type: 'expression',
    returnType: async () => 'string',
    helpText: `Any image or svg URL or a SVG string. 

Example Dynamic SVG String Expression:
\`<svg...> ... fill="\${$.theme.palette.text.primary}" ...</svg>\`
`,
  },
  display: {
    label: 'Display',
    type: 'select',
    required: true,
    options: { block: 'Block', flex: 'Flex', inline: 'Inline' },
  },
  flexDirection,
  alignItems,
  alignContent,
  alignSelf: {
    label: 'Align Self',
    type: 'select',
    options: toOptions([
      'auto',
      'baseline',
      'center',
      'end',
      'flex-end',
      'flex-start',
      'inherit',
      'initial',
      'normal',
      'revert',
      'self-end',
      'self-start',
      'start',
      'stretch',
      'unset',
    ]),
    category: 'Flex',
  },
  justifyContent,
  flexWrap: {
    label: 'Flex Wrap',
    type: 'select',
    required: true,
    options: toOptions(['nowrap', 'wrap', 'wrap-reverse']),
    category: 'Flex',
  },
  flexGrow: {
    label: 'FlexGrow',
    type: 'number',
    min: 0,
    max: 12,
    category: 'Flex',
  },
  flexShrink: {
    label: 'Flex Shrink',
    type: 'number',
    min: 0,
    max: 12,
    category: 'Flex',
  },
  gap,
  backgroundImage: {
    label: 'BG Image',
    type: 'ImgAttributeChooser',
    helpText:
      'Choose an image from the dropdown.For any custom images, Upload them on the Image panel and then select them here.',
  },
  background: {
    label: 'Backround',
    type: 'expression',
    returnType: async () => 'string',
    helpText: 'CSS background property.',
  },
  bgcolor: {
    label: 'Backround Color',
    type: 'ColorExpression',
    helpText:
      'e.g. background.paper or background.default or primary.main or primary.dark or primary.light or any valid CSS color string',
  },
  hoverBgcolor: {
    label: 'Hover Backround Color',
    type: 'ColorExpression',
    helpText:
      'e.g. background.paper or background.default or primary.main or primary.dark or primary.light or any valid CSS color string',
  },
  boxHeight: {
    label: 'Height',
    type: 'string',
    category: 'Size',
    helpText:
      "Any value between 0 & 1 are calculated relative to the parent container's height. e.g. a value of 1 is considered as 100% of the parent's height.",
    placeholder: 'e.g. 1 or 200px',
  },
  boxWidth: {
    label: 'Width',
    type: 'string',
    category: 'Size',
    helpText:
      "Any value between 0 & 1 are calculated relative to the parent container's width. e.g. a value of 1 is considered as 100% of the parent's width.",
    placeholder: 'e.g. 1 or 200px',
  },
  borderRadius: {
    label: 'Border Radius',
    type: 'string',
    placeholder: 'e.g. 16px or 50%',
  },
  boxShadow: {
    label: 'Box Shadow',
    type: 'string',
  },
  color: {
    label: 'Font Color',
    type: 'ColorExpression',
    category: 'Font',
    helpText:
      'e.g. text.primary or text.secondary or primary.main or primary.dark or primary.light or any valid CSS color string',
  },
  hoverColor: {
    label: 'Hover Font Color',
    type: 'ColorExpression',
    category: 'Font',
    helpText:
      'e.g. text.primary or text.secondary or primary.main or primary.dark or primary.light or any valid CSS color string',
  },
  onHoverAnimation: {
    label: 'On Hover Animation',
    type: 'boolean',
  },
  fontSize: {
    label: 'Font Size',
    type: 'string',
    category: 'Font',
  },
  fontWeight: {
    label: 'Font Weight',
    type: 'select',
    options: toOptions([
      '300',
      '400',
      '500',
      '600',
      '700',
      'bold',
      'bolder',
      'lighter',
      'normal',
      'unset',
    ]),
    category: 'Font',
  },
  fontStyle: {
    label: 'Font Style',
    type: 'select',
    options: toOptions(['italic', 'normal', 'oblique']),
    category: 'Font',
  },
  maxHeight: {
    label: 'Max Height',
    type: 'string',
    category: 'Size',
  },
  maxWidth: {
    label: 'Max Width',
    type: 'string',
    category: 'Size',
  },
  minWidth: {
    label: 'Min Width',
    type: 'string',
    category: 'Size',
  },
  minHeight: {
    label: 'Min Height',
    type: 'string',
    category: 'Size',
  },
  overflow: {
    label: 'Overflow',
    type: 'select',
    options: toOptions(['scroll', 'hidden', 'unset', 'auto']),
  },
  margin: {
    label: 'Margin',
    type: 'margin',
    category: 'Spacing',
  },
  padding: {
    label: 'Padding',
    type: 'padding',
    category: 'Spacing',
  },
  border: {
    label: 'Border',
    type: 'border',
  },
  showOnlyOnHoverItemIds: {
    label: 'Show Only On Hover',
    type: 'MultiItemID',
    validateItemId: (value, { platform, pageId, appUid, itemId }) => {
      const found = platform.findParent(
        appUid,
        pageId,
        value,
        (c) => c.itemId === itemId,
      );
      if (!found) {
        return `Unable to find a child component with this item id '${value}' inside this Box!`;
      }
      return '';
    },
    helpText:
      'Choose one or more Item IDs of the child components to be only visible on the mouse hover of this Box component.',
  },
  ...toolTipProps,
};

export function isPropertyVisible<K extends keyof Box>(
  property: K,
  itemData: Required<Box>,
) {
  switch (property) {
    case 'alt':
    case 'src':
      return itemData.component === 'img';
    case 'flexDirection':
    case 'alignItems':
    case 'justifyContent':
    case 'alignContent':
    case 'alignSelf':
    case 'flexGrow':
    case 'flexShrink':
    case 'flexWrap':
    case 'gap':
      return itemData.display === 'flex';
    case 'minHeight':
    case 'maxHeight':
    case 'minWidth':
    case 'maxWidth':
    case 'boxWidth':
    case 'boxHeight':
      return itemData.display !== 'inline';
    case 'bgcolor':
      return isEmpty(itemData.background?.value);
    case 'background':
      return (
        isEmpty(itemData.bgcolor?.value) && isEmpty(itemData.backgroundImage)
      );
    default:
      return true;
  }
}
